import type { ReactNode } from "react";

import { BottomSheet } from "../../BottomSheet";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { CrossIcon } from "../../Icons";
import { Typo } from "../../Typo";

type MarketingBannerConfigurationModalShellProps = {
  isOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
  showDesktopHeader?: boolean;
  modalClassName?: string;
  contentContainerClassName?: string;
  footerContainerClassName?: string;
  contentOverflow?: "auto" | "visible";
};

export const MarketingBannerConfigurationModalShell = ({
  isOpen,
  isMobile,
  onClose,
  title,
  children,
  footer,
  maxWidth = 968,
  showDesktopHeader = true,
  modalClassName,
  contentContainerClassName,
  footerContainerClassName,
  contentOverflow = "auto",
}: MarketingBannerConfigurationModalShellProps) => {
  // Mobile renders a BottomSheet, which locks scroll itself.
  useBodyScrollLock(isOpen && !isMobile);

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        footer={footer}
        isDismissable={false}
      >
        {children}
      </BottomSheet>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div
        className={`relative mx-4 flex max-h-[calc(100vh-32px)] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl ${modalClassName ?? ""}`}
        style={{ maxWidth }}
      >
        {showDesktopHeader && (
          <div className="mb-4 flex shrink-0 flex-col px-8 pt-8">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 transition-colors hover:bg-surface-tonal-default"
                aria-label="Close"
              >
                <CrossIcon className="h-6 w-6 text-icon-primary" />
              </button>
            </div>
            {title && (
              <Typo element="h2" type="headingXL">
                {title}
              </Typo>
            )}
          </div>
        )}

        <div
          className={`flex min-h-0 flex-1 flex-col ${contentOverflow === "visible" ? "overflow-visible" : "overflow-auto"}`}
        >
          <div
            className={`flex flex-col gap-6 px-8 ${contentContainerClassName ?? ""}`}
          >
            {children}
          </div>
        </div>

        {footer && (
          <div
            className={`mt-6 shrink-0 px-8 pb-8 ${footerContainerClassName ?? ""}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
